import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Alert, 
  Badge, InputGroup, Form, Dropdown, Modal, Tabs, Tab, Spinner
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaCogs, FaShieldAlt } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../../components/layout/AdminLayout';
import supabase from '../../utils/supabaseClient';

const ProfileManagement = () => {
  const { isAdmin } = useAuth();
  
  // Estados principais
  const [systemRoles, setSystemRoles] = useState([]);
  const [systems, setSystems] = useState([]);
  const [permissions, setPermissions] = useState([]); // Novo estado para permissões
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedRole, setSelectedRole] = useState(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_id: '',
    permissions: {}, // Mudança: usar permissões específicas do banco
    is_default: false,
    active: true
  });

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [systemFilter, setSystemFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (isAdmin) {
      loadAllData();
    }
  }, [isAdmin]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSystemRoles(),
        loadSystems(),
        loadPermissions() // Novo: carregar permissões do banco
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do sistema');
    } finally {
      setLoading(false);
    }
  };

  const loadSystemRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('system_roles')
        .select(`
          *,
          systems:system_id (
            id, name, slug, icon
          )
        `)
        .order('system_id, name');

      if (error) throw error;
      setSystemRoles(data || []);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      throw error;
    }
  };

  const loadSystems = async () => {
    try {
      const { data, error } = await supabase
        .from('systems')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setSystems(data || []);
    } catch (error) {
      console.error('Erro ao carregar sistemas:', error);
      throw error;
    }
  };

  // Nova função para carregar permissões do banco
  const loadPermissions = async () => {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('name');

      if (error) throw error;
      setPermissions(data || []);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      throw error;
    }
  };

  const openModal = (type, role = null) => {
    setModalType(type);
    setSelectedRole(role);
    setShowModal(true);

    if (type === 'edit' && role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        system_id: role.system_id || '',
        permissions: role.permissions || {}, // Carregar permissões existentes
        is_default: role.is_default || false,
        active: role.active !== false
      });
    } else {
      resetForm();
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRole(null);
    setModalType('create');
    resetForm();
    setError(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      system_id: '',
      permissions: {},
      is_default: false,
      active: true
    });
  };

  // Nova função para gerenciar permissões específicas
  const handlePermissionChange = (permissionName, granted) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionName]: granted
      }
    }));
  };

  const handleCreateRole = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validações
      if (!formData.name || !formData.system_id) {
        throw new Error('Nome e sistema são obrigatórios');
      }

      // Verificar se já existe um perfil com esse nome no sistema
      const { data: existingRole, error: checkError } = await supabase
        .from('system_roles')
        .select('id')
        .eq('system_id', formData.system_id)
        .eq('name', formData.name)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingRole) {
        throw new Error('Já existe um perfil com este nome neste sistema');
      }

      // Criar perfil
      const { error: insertError } = await supabase
        .from('system_roles')
        .insert({
          name: formData.name,
          description: formData.description,
          system_id: formData.system_id,
          permissions: formData.permissions,
          is_default: formData.is_default,
          active: formData.active
        });

      if (insertError) throw insertError;

      setSuccess(`Perfil "${formData.name}" criado com sucesso!`);
      closeModal();
      loadSystemRoles();

    } catch (error) {
      console.error('Erro ao criar perfil:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name || !formData.system_id) {
        throw new Error('Nome e sistema são obrigatórios');
      }

      // Atualizar perfil
      const { error: updateError } = await supabase
        .from('system_roles')
        .update({
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          is_default: formData.is_default,
          active: formData.active
        })
        .eq('id', selectedRole.id);

      if (updateError) throw updateError;

      setSuccess(`Perfil "${formData.name}" atualizado com sucesso!`);
      closeModal();
      loadSystemRoles();

    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setError('Erro ao atualizar perfil: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o perfil "${roleName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('system_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;

      setSuccess(`Perfil "${roleName}" removido com sucesso!`);
      await loadSystemRoles();
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      setError(`Erro ao excluir perfil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.name && formData.system_id;
  };

  const filteredRoles = systemRoles.filter(role => {
    const matchesSearch = 
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.systems?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSystem = systemFilter === 'all' || 
      role.system_id?.toString() === systemFilter;

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && role.active) ||
      (statusFilter === 'inactive' && !role.active);

    return matchesSearch && matchesSystem && matchesStatus;
  });

  const formatarData = (dataString) => {
    return new Date(dataString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="text-center mt-5">
        <h3>Acesso Negado</h3>
        <p>Você não tem permissão para acessar esta área.</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Container fluid>
        {/* Cabeçalho */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-1">
                  <FaShieldAlt className="me-2" />
                  Gerenciamento de Perfis
                </h1>
                <p className="text-muted mb-0">
                  Configure perfis de acesso e suas permissões para cada sistema
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  onClick={loadAllData}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => openModal('create')}
                >
                  <FaPlus className="me-2" />
                  Novo Perfil
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Mensagens */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nome, descrição ou sistema..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={systemFilter}
                  onChange={(e) => setSystemFilter(e.target.value)}
                >
                  <option value="all">Todos os sistemas</option>
                  {systems.map(system => (
                    <option key={system.id} value={system.id}>
                      {system.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Apenas ativos</option>
                  <option value="inactive">Apenas inativos</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => {
                    setSearchTerm('');
                    setSystemFilter('all');
                    setStatusFilter('all');
                  }}
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabela de Perfis */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Perfis Cadastrados ({filteredRoles.length})
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Perfil</th>
                  <th>Sistema</th>
                  <th>Permissões</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map(role => (
                  <tr key={role.id}>
                    <td>
                      <div>
                        <strong>{role.name}</strong>
                        {role.is_default && (
                          <Badge bg="info" className="ms-2">Padrão</Badge>
                        )}
                        <br />
                        <small className="text-muted">{role.description}</small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-2">{role.systems?.icon}</span>
                        {role.systems?.name}
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">
                        {Object.keys(role.permissions || {}).filter(key => role.permissions[key] === true).length} permissões
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={role.active ? 'success' : 'danger'}>
                        {role.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatarData(role.created_at)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          Ações
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openModal('edit', role)}>
                            <FaEdit className="me-2" />
                            Editar
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            className="text-danger"
                            onClick={() => handleDeleteRole(role.id, role.name)}
                          >
                            <FaTrash className="me-2" />
                            Excluir
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {filteredRoles.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-muted">Nenhum perfil encontrado.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Perfil */}
        <Modal show={showModal} onHide={closeModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalType === 'create' ? 'Novo Perfil' : 'Editar Perfil'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-0">
            {error && (
              <div className="px-4 pt-3">
                <Alert variant="danger">{error}</Alert>
              </div>
            )}
            
            <Tabs defaultActiveKey="dados">
              <Tab eventKey="dados" title="📋 Dados do Perfil">
                <div className="p-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Nome do Perfil *</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            name: e.target.value
                          }))}
                          placeholder="Ex: Administrador, Vendedor, Consultor"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sistema *</Form.Label>
                        <Form.Select
                          value={formData.system_id}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            system_id: e.target.value
                          }))}
                        >
                          <option value="">Selecione o sistema...</option>
                          {systems.map(system => (
                            <option key={system.id} value={system.id}>
                              {system.icon} {system.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            description: e.target.value
                          }))}
                          placeholder="Descreva as responsabilidades deste perfil..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label={formData.is_default ? "Perfil Padrão" : "Perfil Normal"}
                        checked={formData.is_default}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          is_default: e.target.checked
                        }))}
                      />
                      <Form.Text className="text-muted">
                        Perfis padrão são atribuídos automaticamente a novos usuários
                      </Form.Text>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label={formData.active ? "Perfil Ativo" : "Perfil Inativo"}
                        checked={formData.active}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          active: e.target.checked
                        }))}
                      />
                    </Col>
                  </Row>
                </div>
              </Tab>

              <Tab eventKey="permissoes" title="🔐 Permissões">
                <div className="p-4">
                  <Alert variant="info">
                    Selecione as permissões específicas que este perfil deve ter no sistema escolhido.
                  </Alert>

                  {permissions.length > 0 ? (
                    <Row className="g-3">
                      {permissions.map(permission => (
                        <Col md={6} key={permission.id}>
                          <Card className={`h-100 ${formData.permissions[permission.name] ? 'border-success' : 'border-light'}`}>
                            <Card.Body>
                              <Form.Check
                                type="switch"
                                id={`permission-${permission.id}`}
                                label={
                                  <div>
                                    <strong>{permission.name}</strong>
                                    {permission.description && (
                                      <div className="text-muted small mt-1">
                                        {permission.description}
                                      </div>
                                    )}
                                  </div>
                                }
                                checked={formData.permissions[permission.name] === true}
                                onChange={(e) => handlePermissionChange(permission.name, e.target.checked)}
                              />
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted">Nenhuma permissão encontrada no sistema.</p>
                    </div>
                  )}

                  {/* Resumo das permissões selecionadas */}
                  <div className="mt-4 p-3 bg-light rounded">
                    <h6>Permissões Selecionadas:</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(formData.permissions || {})
                        .filter(([_, granted]) => granted === true)
                        .map(([permissionName]) => (
                          <Badge key={permissionName} bg="success">
                            {permissionName}
                          </Badge>
                        ))}
                      {Object.keys(formData.permissions || {}).filter(key => formData.permissions[key] === true).length === 0 && (
                        <span className="text-muted">Nenhuma permissão selecionada</span>
                      )}
                    </div>
                  </div>
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={modalType === 'create' ? handleCreateRole : handleUpdateRole}
              disabled={loading || !isFormValid()}
            >
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              {modalType === 'create' ? 'Criar Perfil' : 'Atualizar Perfil'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </AdminLayout>
  );
};

export default ProfileManagement;